import React, { useMemo } from 'react';
import { EquipmentSummaryItem } from './createCustomExxonMobilAutReport';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import Conditional from '../../common/conditional';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import CustomVisualInspectionSummary from '../custom-utm/custom-utm-summary';
import CustomDamageClass from '../custom-general/custom-damage-class';
import CustomUTMBreakdown from '../custom-utm/custom-utm-breakdown';
import CustomUTMReportPDF from '../custom-utm/custom-utm-report';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import EquipmentAnalytics from '../custom-visual-inspection/report-items/equipmentAnalytics';
import ExxonMobilAutSummarySheet from './summary-sheet/autSummarySheet';
import ExxonmobilAutGeneralInformation from './general-information/generalInformation';
import ExecutiveSummary from '../custom-visual-inspection/executive-summary';
import IsometricDrawing from '../custom-utm/isometric-drawing';
import OverviewSheet from '../custom-general/overview-sheet';
import PID from '../custom-visual-inspection/pid';
import ScanData from './scan-data/scanData';
import ScanSummary from './scan-summary';

import appConfig from '../../../utils/config';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface CustomExxonMobilAutReportProps {
	coverData: models.ExxonMobilFVIPipingCoverData;
	equipmentSummary: EquipmentSummaryItem;
	generalDrawing: models.PDFImageProps[];
	generalInfo: models.GeneralInformationData;
	pidData: models.PIDData;
	photoRecord: models.PDFImageProps[];
	projectDetails: models.Project;
	autScanData: models.AutScanDataItem[];
	scanPlan: models.PDFImageProps[];
	summaryData: models.CustomAutSummary;
	reportTitle: string;
	utInspectionItems: models.UTMInspection[];
	utInspectionOverview: models.CustomUTMOverview;
}

const CustomExxonMobilAutReport: React.FC<CustomExxonMobilAutReportProps> = ({
	coverData,
	equipmentSummary,
	generalDrawing,
	generalInfo,
	photoRecord,
	pidData,
	autScanData,
	scanPlan,
	summaryData,
	projectDetails,
	reportTitle,
	utInspectionItems,
	utInspectionOverview
}) => {
	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. RusselSmith Test Procedure',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

	const overview = useCoverSheetOverview();

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(projectDetails.inspectionTypeName)} />

			<CustomInfoSheet
				projectDetails={projectDetails}
				workOrderNumber={coverData.workOrderNumber}
				reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

			<CustomDamageClass />

			<ExecutiveSummary summary={summaryData.executiveSummary} />

			<ExxonMobilAutSummarySheet 
				sectionTitle="Summary"
				sectionSubtitle="General Summary"
				conclusion={summaryData.conclusion} 
				recommendation={summaryData.recommendation} 
			/>

			<CustomVisualInspectionSummary
				anomalyItems={equipmentSummary.anomalyItems}
				sectionTitle="Summary"
				sectionSubtitle="Visual Inspection Summary"
			/>

			<ExxonmobilAutGeneralInformation {...generalInfo} />

			<PID
				hasPID={pidData.hasPID}
				pidImages={pidData.pIDImages} 
				pidNumber={pidData.pIDNumber} 
				sectionTitle="P & ID"
			/>

			<IsometricDrawing 
				label="General Drawing" 
				isoImages={generalDrawing}  
			/>

			<ScanSummary 
				scanDataItems={autScanData} 
				utmDataItems={utInspectionItems} 
			/>

			<CustomUTMBreakdown
				corrosionAllowance={utInspectionOverview.corrosionAllowance}
				inspectionItems={utInspectionItems} 
				summaryTitle='UTM Summary'
				expressSummaryTitle='UTM Summary'
				expressSummarySubtitle='UTM Express Summary'
			/>

			<Conditional isVisible={appConfig.enableFVIReportEmbedding}>
				<EquipmentAnalytics
					useGroupedTitle
					items={[
						{
							hasPDO: "no",
							pdoChecklist: [],
							hasPID: "no",
							equipmentNumber: equipmentSummary.equipmentNumber,
							anomalySummary: equipmentSummary.anomalySummary, 
							hasAnomalies: equipmentSummary.hasAnomalies, 
							inspectionType: 'external',
							additionalComment: '',
							checklistItems: equipmentSummary.checklistItems,
							anomalyItems: equipmentSummary.anomalyItems,
							pIDImages: [],
							pIDNumber: '',
							generalPhotos: [],
							id: 1,
							inspectionDescription: 'No anomaly in visual inspection'
						}
					]}
				/>
			</Conditional>

            <IsometricDrawing label="Scan Plan" isoImages={scanPlan} />

			<ScanData scanDataItems={autScanData} />

			<CustomUTMReportPDF
				title="" 
				tmlLocations={[]}
				utInspectionItems={utInspectionItems}
				inspectionOverview={utInspectionOverview}
			/>

			<CustomGeneralPhotos
				label="General Photos"
				photos={photoRecord}
			/>
		</React.Fragment>
	);
};

export default withPDF(CustomExxonMobilAutReport);
